<template>
  <YapListBox
    v-model="modelValue"
    :options="state.listBoxOptions"
    :grouped="grouped"
    :floating="floating"
    :readonly="state.listBoxOptions.length === 1 || disabled"
    v-bind="$attrs"
  >
    <template #trigger="{ selectedOption }">
      <div class="-mt-1 flex items-center gap-2 dark:text-primary-dark-100">
        <YapCurrencyIcon v-if="selectedOption" :currency="selectedOption.value" class="size-4" />
        {{ selectedOption?.value }}
      </div>
    </template>
    <template #option="{ option, selected }">
      <div class="flex items-center justify-between">
        <div>
          <div class="flex gap-2" :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">
            <span class="min-w-12">{{ option.value }}</span>
            <span>{{ option.name }}</span>
          </div>
        </div>
        <YapCurrencyIcon :currency="option.value" class="size-4" />
      </div>
    </template>
  </YapListBox>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from 'vue';

import apiGw from '@/services/apiGw';
import router from '@/router/router';
import { useI18n } from '@/plugins/i18n';
import YapListBox from '@/components/YapListBox.vue';
import { useRefdataStore } from '@/store/refdataStore';
import YapCurrencyIcon from '@/components/YapCurrencyIcon.vue';
import { YapListBoxOption } from '@/components/YapListBox.vue';

const modelValue = defineModel<string>();
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const refdataStore = useRefdataStore();

const props = defineProps<{
  grouped?: boolean;
  floating?: boolean;
  disabled?: boolean;
}>();

interface State {
  isSwiftPayment: boolean;
  listBoxOptions: Array<YapListBoxOption>;
  currencies: Array<{ value: string; name: string; extraData: { currency: string; totalValuation: number } }>;
}

const state: State = reactive({
  isSwiftPayment: computed(() => router.currentRoute.value.query.paymentType === 'swift'),
  listBoxOptions: computed(() => {
    return state.currencies.map((currency) => ({
      value: currency.value,
      name: currency.name,
      extraData: currency.extraData
    }));
  }),
  currencies: []
});

onMounted(async () => {
  refdataStore.loadCollections(['currencies']);
  loadCurrencies();
});

interface Currency {
  currencyCode: string;
  isPopular: boolean;
  countries?: string[];
}

async function loadCurrencies() {
  const response = await apiGw.get(`v2/incore/currencies/CHF`);
  const formatted = response.data.map((currencyData: Currency) => {
    return {
      value: currencyData.currencyCode,
      name: t(`currencies.${currencyData.currencyCode}`),
      extraData: {
        currency: currencyData.currencyCode
      }
    };
  });
  state.currencies = formatted;
}
</script>
